/**
 * useImportFailureToast
 *
 * Subscribes to the SSE `sync_failed` stream and surfaces a global ERROR toast
 * so a failed background import is never silent — the user is actively told the
 * import failed (and why, using the SAFE backend message).
 *
 * Sibling of `usePostSyncSummaryToast` (completed toasts). Intended to mount
 * once in `AuthenticatedLayout`.
 *
 * SECURITY: `event.error` is NOT safe to render. Only the CSV/DB failures go
 * through the backend's category mapper (import-failure-message.ts); a wallet
 * job's `error.message` is the raw provider text and reached the customer
 * verbatim — English, JSON-RPC codes, and a third-party upsell URL
 * ("...order a dedicated full node here: https://www.allnodes.com/..."). Every
 * message is therefore routed through `formatJobFailureForDisplay`, the SAME
 * mapping the sources table already applies, which prefers the failure CODE
 * (#2215), replaces RPC noise with a localized sentence and strips URLs from
 * anything else. A message-less, code-less event falls back to a localized
 * generic string.
 *
 * Behavior:
 *   - Dedupes per `jobId` so a replayed `sync_failed` event (2s poll re-emit,
 *     reconnect, StrictMode double-mount) does NOT stack the same toast twice.
 */

import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'
import type { SseSyncEvent } from '@/platform/sync/types'
import { syncClient } from '@/platform/websocket/syncClient'
import { useNotifications } from '@/shared/components/Notification'

import { formatJobFailureForDisplay } from '../utils/format-sync-error'
import { createSeenJobIdTracker } from '../utils/seen-job-id-tracker'

/**
 * Module-level dedup tracker — survives StrictMode double-mounts and
 * re-renders. Bounded FIFO, see createSeenJobIdTracker.
 */
const seenFailedJobIdTracker = createSeenJobIdTracker()

export const useImportFailureToast = (): void => {
  const { show } = useNotifications()
  const { t } = useTranslation('imports')
  // `resolveSyncSourceLabel` resolves `monitoring.unknownSource`, which lives in
  // the COMMON namespace — passing the imports-bound `t` would print the raw key.
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    const handleSyncFailed = (event: SseSyncEvent): void => {
      if (!event.jobId || seenFailedJobIdTracker.hasSeen(event.jobId)) return
      seenFailedJobIdTracker.markSeen(event.jobId)

      // ⚠️ El CÓDIGO manda sobre la prosa (#2215). El desenlace real de la
      // importación medida era `STUCK_JOB_REAPED`, y su `message` es jerga de
      // operación («worker likely crashed or hung») cuando llega, o nada cuando
      // no. Con el código, la causa tiene nombre accionable; sin él se sigue
      // mostrando el mensaje almacenado, que lleva más información que una
      // etiqueta genérica (el fallo de cabeceras compara las dos listas).
      const hasFailureCode =
        typeof event.errorCode === 'string' && event.errorCode.trim().length > 0
      const hasStoredMessage = typeof event.error === 'string' && event.error.trim().length > 0
      const safeMessage =
        hasFailureCode || hasStoredMessage
          ? formatJobFailureForDisplay(
              {
                message: event.error,
                code: event.errorCode,
                // Afina la siega: «el proceso se detuvo» y «siguió vivo sin
                // avanzar» comparten código y no comparten noticia (#2205).
                reapReason: event.reapReason,
              },
              t,
            )
          : t('progressIndicator.failed.genericMessage', 'The import failed.')

      show({
        type: 'error',
        // `message` is the bold heading (the source that failed); `content`
        // carries the safe failure reason. Errors don't auto-dismiss
        // (duration 0) so the user cannot miss a lost import.
        //
        // The name goes through the same resolver the history panel uses: the
        // worker-push relay can omit `sourceName`, which rendered a heading of
        // "Importación fallida:" with nothing after the colon — with several
        // wallets configured the user could not tell which one broke.
        message: t('progressIndicator.failed.toast', 'Import failed: {{sourceName}}', {
          sourceName: resolveSyncSourceLabel(event.sourceName, tCommon),
        }),
        content: safeMessage,
        duration: 0,
      })
    }

    const unsubscribe = syncClient.on('sync_failed', handleSyncFailed)
    // Acquire a refcount on the shared SSE connection (see syncClient.ts).
    syncClient.connect()

    return (): void => {
      unsubscribe()
      syncClient.disconnect()
    }
    // `show` + `t` + `tCommon` are stable refs from their providers — re-running this
    // effect would tear down + re-open the SSE connection unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
